// Over-Under — a provably-fair hi-lo cash-out ladder on Variance Vault.
//
// A card is dealt. You call the NEXT card over or under. Get it right and you climb a rung;
// the ladder multiplier grows 1.30x per rung. Bank at any rung, or push your luck to rung 12.
// A tie is a push: the round ends and pays whatever the ladder is worth right now (1x at rung 0).
//
// Ranks are A(1)..K(13), drawn uniformly WITH REPLACEMENT (an infinite shoe) — so no card
// counting, and every rung is the same honest deck.
//
//   at card r:  over wins if r' > r   -> (13-r)/13
//               under wins if r' < r  -> (r-1)/13
//               tie r' == r           -> 1/13, pays the current rung
//
// The house edge lives entirely in the FORCED first guess at rung 0 (no banking before you
// have called at least one card). Every later rung is a free choice, so the tree's root value
// is the value of PERFECT play — the contract seals against that, not against a hopeful line.

import { defineGame } from "@freeroll/sdk/v14";

export const RANKS = 13;
export const RANK_NAMES = [, "A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];

/** Ladder payout in ppm at rung k (k >= 1); rung 0 has no bank action. */
function ladderPpm(scalePpm, growthNum, growthDen, k) {
  let v = BigInt(scalePpm);
  for (let i = 1; i < k; i++) v = (v * BigInt(growthNum)) / BigInt(growthDen);
  return Number(v);
}

/** Build the spec for a given ladder scale. Payouts are linear in `scalePpm`, so a single
 *  bisection on it lands the root anywhere in the band we like. */
export function overUnderSpec({ name, rungs, scalePpm, growthNum = 130, growthDen = 100 }) {
  const M = [0];
  for (let k = 1; k <= rungs; k++) M.push(ladderPpm(scalePpm, growthNum, growthDen, k));
  // What a push (tie) pays at rung k: your stake back at rung 0, else the current ladder rung.
  const pushPpm = (k) => (k === 0 ? 1_000_000 : M[k]);

  const pay = (ppm) => ({ t: "pay", ppm });
  const card = (k, r) => (k >= rungs ? pay(M[rungs]) : { t: "card", k, r });

  const spec = {
    name,
    root: { t: "deal" },
    rules: (s) => {
      if (s.t === "pay") return { type: "payout", multiplierPpm: s.ppm };

      // Deal the opening card.
      if (s.t === "deal")
        return {
          type: "chance",
          outcomes: Array.from({ length: RANKS }, (_, i) => ({
            weight: 1, next: card(0, i + 1), label: RANK_NAMES[i + 1],
          })),
        };

      // A dealt card: call it, or bank what you have.
      if (s.t === "card") {
        const actions = [
          { name: "over", next: { t: "draw", k: s.k, r: s.r, side: "over" } },
          { name: "under", next: { t: "draw", k: s.k, r: s.r, side: "under" } },
        ];
        if (s.k >= 1) actions.push({ name: "bank", next: pay(M[s.k]) });
        return { type: "decision", actions };
      }

      // The reveal. EVERY rank is its own outcome — winners carry the next rung's card, losers
      // all point at the same zero leaf but keep their own label. Collapsing the losers into one
      // "bust" child (which fits just as well inside MAX_NODE_CHILDREN) would save child entries
      // but throw away WHICH card beat you: the chain would never record it, so a UI could not
      // show the losing card without inventing it. 13 children ≤ 32, and the node count is
      // identical either way, so the honest version is free.
      const outcomes = [];
      for (let s2 = 1; s2 <= RANKS; s2++) {
        if (s2 === s.r) continue;
        const win = s.side === "over" ? s2 > s.r : s2 < s.r;
        outcomes.push({ weight: 1, next: win ? card(s.k + 1, s2) : pay(0), label: RANK_NAMES[s2] });
      }
      outcomes.push({ weight: 1, next: pay(pushPpm(s.k)), label: "push" });
      return { type: "chance", outcomes };
    },
  };
  // Callers get the ladder WITHOUT M[0] (the rung-0 sentinel, which is not a bankable rung):
  // index i is rung i+1. Returning the padded array made the UI render a phantom "rung 1 = 0.00x"
  // and shift every real rung down by one.
  return { spec, ladder: M.slice(1) };
}

/** Bisect the ladder scale so the compiled root lands on `targetPpm` (mid-band by default:
 *  980_000 ppm == a 2.0% edge, with ~5000 ppm of headroom on either side of [975k, 985k]
 *  so integer ceil drift can never knock it out of the band). */
export function buildOverUnder({ name, rungs, targetPpm = 980_000, growthNum = 130, growthDen = 100 }) {
  const compileAt = (scalePpm) => {
    const { spec, ladder } = overUnderSpec({ name, rungs, scalePpm, growthNum, growthDen });
    return { compiled: defineGame(spec, { unsealed: true }), ladder };
  };
  let lo = 100_000, hi = 3_000_000;
  for (let i = 0; i < 42; i++) {
    const mid = Math.floor((lo + hi) / 2);
    if (compileAt(mid).compiled.rootValuePpm < targetPpm) lo = mid; else hi = mid;
  }
  // Take the largest scale that still seals at or below target (player-favorable side of the tick).
  const scalePpm = compileAt(lo).compiled.rootValuePpm <= targetPpm ? lo : hi;
  const { spec, ladder } = overUnderSpec({ name, rungs, scalePpm, growthNum, growthDen });
  return { compiled: defineGame(spec), ladder, scalePpm };   // ladder[i] === payout at rung i+1
}

export const OVER_UNDER = { name: "over-under", rungs: 12 };
export const OVER_UNDER_SPRINT = { name: "over-under-sprint", rungs: 5 };
